import {
  findCoding,
  findIdentifier,
  parseReference,
} from '@lifeomic/fhir-constants';
import { fhirSystems } from '../OnboardingSnapshot/fhir-systems';
import type {
  FileValidationInvalidReason,
  InvalidFileDocumentReference,
} from './types';

export const RESOURCE_VISIBILITY_SYSTEM =
  'http://lifeomic.com/fhir/resource-visibility';
export const CLINIC_ONLY_CODE = 'clinic-only';
export const ELATION_FILE_SOURCE_TYPE_CODE = 'Elation File';

const SOURCE_TYPE_SYSTEM = 'http://lifeomic.com/fhir/source-type';

export const getFileOwnershipValidationStatus = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  findCoding(documentReference?.meta?.tag, {
    system: fhirSystems.documentReference.fileOwnershipValidationStatus.system,
  });

export const getInvalidFileReason = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
): FileValidationInvalidReason | undefined => {
  const coding =
    findCoding(documentReference?.meta?.tag, {
      system:
        fhirSystems.documentReference.fileOwnershipValidationReasoningCode
          .system,
    }) || getFileOwnershipValidationStatus(documentReference);

  if (!coding?.code && !coding?.display) {
    return undefined;
  }

  return {
    code: coding.code,
    display: coding.display || coding.code || 'Invalid file',
    system: coding.system,
  };
};

export const isClinicOnlyDocumentReference = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  !!findCoding(documentReference?.meta?.tag, {
    system: RESOURCE_VISIBILITY_SYSTEM,
    code: CLINIC_ONLY_CODE,
  });

export const isInvalidFileDocumentReference = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  getFileOwnershipValidationStatus(documentReference)?.code ===
  fhirSystems.documentReference.fileOwnershipValidationStatus.values.invalid;

export const isElationFileDocumentReference = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  !!findCoding(documentReference?.meta?.tag, {
    system: SOURCE_TYPE_SYSTEM,
    code: ELATION_FILE_SOURCE_TYPE_CODE,
  });

export const isCurrentInvalidFileDocumentReference = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  isInvalidFileDocumentReference(documentReference) &&
  isElationFileDocumentReference(documentReference);

export const isHiddenByValidationDocumentReference = (
  documentReference?: Pick<InvalidFileDocumentReference, 'meta'>,
) =>
  isInvalidFileDocumentReference(documentReference) &&
  isClinicOnlyDocumentReference(documentReference);

export const getFileId = (
  documentReference?: Pick<InvalidFileDocumentReference, 'identifier'>,
) =>
  findIdentifier(documentReference?.identifier, {
    system: fhirSystems.documentReference.fileId.system,
  })?.value;

export const getDocumentReferencePatientId = (
  documentReference?: Pick<InvalidFileDocumentReference, 'subject'>,
) => {
  const subject = parseReference(documentReference?.subject);

  return subject?.resourceType === 'Patient' ? subject.id : undefined;
};
